import { Link } from 'react-router-dom';
import { useState, useEffect } from 'react';
import SEOHead from '../../components/SEOHead';

export default function HomePage() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const baseUrl = import.meta.env.VITE_SITE_URL || 'https://jackspumps.ie';

  const homeSchema = [
    {
      "@context": "https://schema.org",
      "@type": "WebPage",
      "@id": `${baseUrl}/#webpage`,
      "url": `${baseUrl}/`,
      "name": "Jack's Pumps | #1 Pump Station Repairs Ireland | 24/7 Emergency Service",
      "description": "Ireland's leading pump station specialists. 24/7 emergency pump repairs, foul pump servicing, blocked pump clearing, pump installation across Dublin, Wicklow, Wexford, Kildare, Meath, Louth, Carlow.",
      "isPartOf": {"@id": `${baseUrl}/#website`},
      "about": {"@id": `${baseUrl}/#organization`},
      "primaryImageOfPage": {
        "@type": "ImageObject",
        "url": "https://static.readdy.ai/image/dc999f92be6fe23ab0f2c081255e30e9/be41bd6c16e3f7ca48ba1bf859508677.jpeg"
      }
    },
    {
      "@context": "https://schema.org",
      "@type": "LocalBusiness",
      "@id": `${baseUrl}/#organization`,
      "name": "Jack's Pumps",
      "alternateName": ["Jacks Pumps", "Jack's Pumps Ireland", "Jacks Pumps Ireland"],
      "description": "Dublin & Leinster's #1 pump station specialists providing 24/7 emergency pump repairs, foul pump station servicing, blocked pump clearing, de-ragging, pump installation and maintenance across Dublin, Leinster, Wicklow, Wexford, Kildare, Meath, Louth, and Carlow. Expert pump engineers available for domestic, commercial and industrial pump systems.",
      "url": baseUrl,
      "telephone": "+35340454753",
      "email": "info@jackspumps.ie",
      "logo": "https://static.readdy.ai/image/dc999f92be6fe23ab0f2c081255e30e9/be41bd6c16e3f7ca48ba1bf859508677.jpeg",
      "image": "https://static.readdy.ai/image/dc999f92be6fe23ab0f2c081255e30e9/be41bd6c16e3f7ca48ba1bf859508677.jpeg",
      "priceRange": "€€",
      "currenciesAccepted": "EUR",
      "paymentAccepted": "Cash, Credit Card, Bank Transfer",
      "address": {
        "@type": "PostalAddress",
        "addressRegion": "Wicklow",
        "addressCountry": "IE"
      },
      "geo": {
        "@type": "GeoCoordinates",
        "latitude": "53.3498",
        "longitude": "-6.2603"
      },
      "serviceArea": {
        "@type": "GeoCircle",
        "geoMidpoint": {
          "@type": "GeoCoordinates",
          "latitude": "53.3498",
          "longitude": "-6.2603"
        },
        "geoRadius": "120000"
      },
      "areaServed": [
        {"@type": "City", "name": "Dublin"},
        {"@type": "AdministrativeArea", "name": "Leinster"},
        {"@type": "City", "name": "Wicklow"},
        {"@type": "City", "name": "Wexford"},
        {"@type": "City", "name": "Kildare"},
        {"@type": "City", "name": "Meath"},
        {"@type": "City", "name": "Louth"},
        {"@type": "City", "name": "Carlow"},
        {"@type": "Country", "name": "Ireland"}
      ],
      "openingHoursSpecification": {
        "@type": "OpeningHoursSpecification",
        "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"],
        "opens": "00:00",
        "closes": "23:59"
      },
      "aggregateRating": {
        "@type": "AggregateRating",
        "ratingValue": "5",
        "reviewCount": "47",
        "bestRating": "5",
        "worstRating": "1"
      },
      "review": [
        {
          "@type": "Review",
          "author": {"@type": "Person", "name": "Michael O'Brien"},
          "reviewRating": {"@type": "Rating", "ratingValue": "5", "bestRating": "5"},
          "reviewBody": "Jack's Pumps responded to our emergency callout within 2 hours. Professional service, clear communication, and the pump station was back running the same day."
        },
        {
          "@type": "Review",
          "author": {"@type": "Person", "name": "Sarah Murphy"},
          "reviewRating": {"@type": "Rating", "ratingValue": "5", "bestRating": "5"},
          "reviewBody": "We've been using Jack's Pumps for our planned maintenance contract for over a year. Reliable, professional, and always on time."
        }
      ]
    },
    {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": [
        {
          "@type": "Question",
          "name": "What areas in Ireland do you cover for pump repairs?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Jack's Pumps provides pump station services across Ireland, with primary coverage in Dublin, Wicklow, Wexford, Kildare, Meath, Louth, and Carlow. We offer 24/7 emergency response throughout these areas and can travel nationwide for larger projects."
          }
        },
        {
          "@type": "Question",
          "name": "Do you offer 24/7 emergency pump repairs in Ireland?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Yes, Jack's Pumps offers 24/7 emergency pump repair services across Ireland. We understand that pump failures can happen at any time and require immediate attention to prevent flooding, sewage backup, or property damage. Call +353 404 54753 for emergency assistance."
          }
        },
        {
          "@type": "Question",
          "name": "What types of pumps do you repair and service?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "We service and repair all types of pump systems including foul water pumps, sewage pumps, wastewater pumps, lift stations, stormwater pumps, surface water pumps, submersible pumps, and commercial/industrial pump stations. We work with domestic, commercial, and industrial clients."
          }
        },
        {
          "@type": "Question",
          "name": "How quickly can you respond to a pump emergency in Dublin?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "For emergency pump repairs in Dublin and surrounding areas, we typically respond within 1-2 hours. Our rapid response team is available 24/7 and carries essential parts and equipment to resolve most pump issues on the first visit."
          }
        },
        {
          "@type": "Question",
          "name": "What causes a pump station to fail?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Common causes of pump station failure include: blockages from debris, wipes, or foreign objects (ragging), electrical faults, worn impellers, float switch failures, control panel issues, and lack of regular maintenance. Regular servicing can prevent most failures."
          }
        },
        {
          "@type": "Question",
          "name": "Do you offer pump maintenance contracts in Ireland?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Yes, we offer planned service contracts for property managers, facilities teams, housing estates, and commercial/industrial sites. Our maintenance contracts include scheduled visits, comprehensive reporting, priority emergency response, and help prevent costly breakdowns."
          }
        },
        {
          "@type": "Question",
          "name": "How much does pump repair cost in Ireland?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Pump repair costs vary depending on the issue, parts required, and urgency. We provide transparent pricing and can give quotes before work begins. Contact us at +353 404 54753 or submit an enquiry for a free assessment and quote."
          }
        },
        {
          "@type": "Question",
          "name": "What is pump de-ragging and when is it needed?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Pump de-ragging is the process of removing debris, wipes, rags, and other materials that have wrapped around the pump impeller causing blockages. Signs you need de-ragging include: pump running but not pumping, high level alarms, slow drainage, or unusual pump noises."
          }
        }
      ]
    },
    {
      "@context": "https://schema.org",
      "@type": "Service",
      "serviceType": "Pump Station Repairs and Maintenance",
      "provider": {"@id": `${baseUrl}/#organization`},
      "areaServed": {"@type": "Country", "name": "Ireland"},
      "hasOfferCatalog": {
        "@type": "OfferCatalog",
        "name": "Pump Services",
        "itemListElement": [
          {
            "@type": "Offer",
            "itemOffered": {
              "@type": "Service",
              "name": "Emergency Pump Repairs Ireland",
              "description": "24/7 emergency pump repair services across Dublin, Wicklow, Wexford, Kildare, Meath, Louth, and Carlow"
            }
          },
          {
            "@type": "Offer",
            "itemOffered": {
              "@type": "Service",
              "name": "Foul Pump Station Servicing",
              "description": "Regular maintenance and servicing of foul water pump stations for domestic, commercial and industrial clients"
            }
          },
          {
            "@type": "Offer",
            "itemOffered": {
              "@type": "Service",
              "name": "Pump De-ragging and Unblocking",
              "description": "Professional pump de-ragging, blockage clearing, and chamber cleaning services"
            }
          },
          {
            "@type": "Offer",
            "itemOffered": {
              "@type": "Service",
              "name": "Pump Installation Ireland",
              "description": "New pump system installation and replacement for all property types"
            }
          }
        ]
      }
    }
  ];

  const faqData = [
    {
      question: "What areas in Ireland do you cover for pump repairs?",
      answer: "Jack's Pumps provides pump station services across Ireland, with primary coverage in Dublin, Wicklow, Wexford, Kildare, Meath, Louth, and Carlow. We offer 24/7 emergency response throughout these areas and can travel nationwide for larger projects."
    },
    {
      question: "Do you offer 24/7 emergency pump repairs in Ireland?",
      answer: "Yes, Jack's Pumps offers 24/7 emergency pump repair services across Ireland. We understand that pump failures can happen at any time and require immediate attention to prevent flooding, sewage backup, or property damage. Call +353 404 54753 for emergency assistance."
    },
    {
      question: "What types of pumps do you repair and service?",
      answer: "We service and repair all types of pump systems including foul water pumps, sewage pumps, wastewater pumps, lift stations, stormwater pumps, surface water pumps, submersible pumps, and commercial/industrial pump stations. We work with domestic, commercial, and industrial clients."
    },
    {
      question: "How quickly can you respond to a pump emergency in Dublin?",
      answer: "For emergency pump repairs in Dublin and surrounding areas, we typically respond within 1-2 hours. Our rapid response team is available 24/7 and carries essential parts and equipment to resolve most pump issues on the first visit."
    },
    {
      question: "What causes a pump station to fail?",
      answer: "Common causes of pump station failure include: blockages from debris, wipes, or foreign objects (ragging), electrical faults, worn impellers, float switch failures, control panel issues, and lack of regular maintenance. Regular servicing can prevent most failures."
    },
    {
      question: "Do you offer pump maintenance contracts in Ireland?",
      answer: "Yes, we offer planned service contracts for property managers, facilities teams, housing estates, and commercial/industrial sites. Our maintenance contracts include scheduled visits, comprehensive reporting, priority emergency response, and help prevent costly breakdowns."
    },
    {
      question: "How much does pump repair cost in Ireland?",
      answer: "Pump repair costs vary depending on the issue, parts required, and urgency. We provide transparent pricing and can give quotes before work begins. Contact us at +353 404 54753 or submit an enquiry for a free assessment and quote."
    },
    {
      question: "What is pump de-ragging and when is it needed?",
      answer: "Pump de-ragging is the process of removing debris, wipes, rags, and other materials that have wrapped around the pump impeller causing blockages. Signs you need de-ragging include: pump running but not pumping, high level alarms, slow drainage, or unusual pump noises."
    }
  ];

  return (
    <>
      <SEOHead
        title="Jack's Pumps | #1 Pump Station Repairs Dublin & Leinster | 24/7 Emergency Service"
        description="Dublin & Leinster's leading pump station specialists. 24/7 emergency pump repairs, foul pump servicing, blocked pump clearing, pump installation across Dublin, Leinster, Wicklow, Wexford, Kildare, Meath, Louth, Carlow. Call +353 404 54753 now."
        canonical="/"
        keywords="pump repairs Dublin, pump station repairs Dublin, emergency pump repairs Dublin, pump repairs Leinster, pump station servicing Leinster, pump engineer Dublin, foul pump repairs Dublin, blocked pump clearing Dublin, pump installation Dublin, pump maintenance Dublin, wastewater pump repairs Dublin, sewage pump repairs Leinster, 24/7 pump repairs Dublin, pump repairs Ireland, pump de-ragging Dublin, pump unblocking Leinster"
        ogTitle="Jack's Pumps | #1 Pump Station Repairs Dublin & Leinster | 24/7 Emergency"
        ogDescription="Dublin & Leinster's leading pump station specialists. 24/7 emergency pump repairs across Dublin, Leinster, Wicklow, Kildare. Call +353 404 54753."
        schema={homeSchema}
      />
      
      <div className="min-h-screen bg-white">
        {/* Navigation */}
        <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${isScrolled ? 'bg-white shadow-lg' : 'bg-transparent'}`} role="navigation" aria-label="Main navigation">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between items-center h-20 lg:h-24 py-2">
              <Link to="/" className="flex items-center -ml-2 sm:-ml-4 lg:ml-0" aria-label="Jack's Pumps Home - Pump Station Repairs Ireland">
                <h1 className="sr-only">Jack's Pumps - Ireland's #1 Pump Station Repair Specialists | 24/7 Emergency Pump Repairs Dublin Wicklow Wexford</h1>
                <img 
                  src="https://static.readdy.ai/image/dc999f92be6fe23ab0f2c081255e30e9/be41bd6c16e3f7ca48ba1bf859508677.jpeg" 
                  alt="Jack's Pumps Logo - Professional Pump Station Repairs Ireland" 
                  title="Jack's Pumps - 24/7 Emergency Pump Repairs Ireland"
                  className="h-16 sm:h-20 lg:h-20 w-auto"
                  width="120"
                  height="80"
                />
              </Link>
              <div className="hidden md:flex items-center space-x-6 lg:space-x-8">
                <Link to="/" className={`text-base lg:text-base font-medium transition-colors ${isScrolled ? 'text-gray-900 hover:text-[#00D084]' : 'text-white hover:text-[#00D084]'}`}>Home</Link>
                <Link to="/services" className={`text-base lg:text-base font-medium transition-colors ${isScrolled ? 'text-gray-900 hover:text-[#00D084]' : 'text-white hover:text-[#00D084]'}`}>Services</Link>
                <Link to="/contact" className={`text-base lg:text-base font-medium transition-colors ${isScrolled ? 'text-gray-900 hover:text-[#00D084]' : 'text-white hover:text-[#00D084]'}`}>Contact</Link>
                <a href="tel:+35340454753" className="bg-[#00D084] text-white px-6 lg:px-8 py-3 lg:py-3.5 rounded-lg font-medium hover:bg-[#00B870] transition-colors whitespace-nowrap text-base" aria-label="Call Jack's Pumps for emergency pump repairs Ireland">
                  Call Now
                </a>
              </div>
              <div className="md:hidden">
                <a href="tel:+35340454753" className="bg-[#00D084] text-white px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap" aria-label="Call Jack's Pumps">
                  Call
                </a>
              </div>
            </div>
          </div>
        </nav>

        {/* Hero Section */}
        <header className="relative min-h-screen flex items-center justify-center">
          <div className="absolute inset-0">
            <img 
              src="https://public.readdy.ai/ai/img_res/15170178c529151a6959dfcd4f168ad2.jpg" 
              alt="Professional pump engineer servicing industrial pump station in Ireland - Jack's Pumps 24/7 emergency repairs" 
              title="Jack's Pumps - Professional Pump Station Servicing Ireland"
              className="w-full h-full object-cover object-top"
              width="1920"
              height="1080"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/75 via-black/70 to-black/75"></div>
          </div>
          
          <div className="relative z-10 w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center py-32">
            <div className="mb-8">
              <h2 className="text-5xl sm:text-6xl lg:text-7xl xl:text-8xl font-extrabold text-white mb-3" style={{ letterSpacing: '-0.02em' }}>
                Jack's Pumps
              </h2>
              <div className="flex items-center justify-center gap-3 mb-6">
                <div className="h-px w-16 lg:w-24 bg-[#00D084]"></div>
                <p className="text-xl sm:text-2xl lg:text-3xl font-medium text-[#00D084]">Pump Problems. Solved</p>
                <div className="h-px w-16 lg:w-24 bg-[#00D084]"></div>
              </div>
            </div>

            <div className="inline-block bg-[#00D084]/20 border border-[#00D084] px-4 lg:px-6 py-2 lg:py-3 rounded-full mb-6">
              <span className="text-[#00D084] text-sm lg:text-base font-medium uppercase tracking-wide">24/7 Emergency Pump Repairs Ireland</span>
            </div>
            
            <p className="text-[#00D084] text-sm lg:text-base uppercase tracking-widest mb-4 font-semibold">#1 Pump Station Specialists • Nationwide Coverage</p>
            
            <strong className="text-3xl sm:text-4xl lg:text-6xl xl:text-7xl font-extrabold text-white mb-6 lg:mb-8 leading-tight max-w-6xl mx-auto block" style={{ textShadow: '0 2px 10px rgba(0,0,0,0.5)' }}>
              Ireland's Leading Pump Repair Experts<br />Serving Dublin, Wicklow & Nationwide
            </strong>
            
            <p className="text-base sm:text-lg lg:text-xl xl:text-2xl text-gray-300 mb-10 lg:mb-12 max-w-4xl mx-auto">
              <strong>Emergency pump repairs</strong>, <strong>foul pump servicing</strong>, <strong>blocked pump clearing</strong> and <strong>pump installation</strong> for domestic, commercial and industrial clients across <strong>Dublin, Wicklow, Wexford, Kildare, Meath, Louth, and Carlow</strong>.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 lg:gap-6 justify-center items-center max-w-4xl mx-auto">
              <a href="tel:+35340454753" className="w-full sm:w-auto bg-[#00D084] text-white px-8 lg:px-10 py-4 lg:py-5 rounded-lg font-bold text-lg lg:text-xl hover:bg-[#00B870] transition-all flex items-center justify-center gap-3 whitespace-nowrap" aria-label="Call Jack's Pumps for emergency pump repairs Ireland +353 404 54753">
                <i className="ri-phone-fill text-2xl lg:text-3xl" aria-hidden="true"></i>
                Call +353 404 54753
              </a>
              <a href="https://wa.me/35340454753" className="w-full sm:w-auto bg-transparent border-2 border-[#00D084] text-[#00D084] px-8 lg:px-10 py-4 lg:py-5 rounded-lg font-bold text-lg lg:text-xl hover:bg-[#00D084] hover:text-white transition-all flex items-center justify-center gap-3 whitespace-nowrap" rel="noopener noreferrer" aria-label="Contact Jack's Pumps via WhatsApp for pump repairs">
                <i className="ri-whatsapp-fill text-2xl lg:text-3xl" aria-hidden="true"></i>
                WhatsApp Us
              </a>
              <a href="#contact" className="w-full sm:w-auto bg-transparent border-2 border-white text-white px-8 lg:px-10 py-4 lg:py-5 rounded-lg font-bold text-lg lg:text-xl hover:bg-white hover:text-[#1A1A1A] transition-all flex items-center justify-center gap-3 whitespace-nowrap" aria-label="Request a callback from Jack's Pumps for pump services">
                Get Free Quote
                <i className="ri-arrow-right-line text-xl lg:text-2xl" aria-hidden="true"></i>
              </a>
            </div>
          </div>
        </header>

        {/* Quick Value Cards */}
        <section className="bg-[#1A1A1A] py-16" aria-labelledby="services-overview">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 id="services-overview" className="sr-only">Our Core Pump Services in Ireland</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {[
                { icon: 'ri-alarm-warning-line', title: '24/7 Emergency Response', desc: 'Rapid emergency pump repairs across Ireland' },
                { icon: 'ri-tools-line', title: 'Pump Servicing & Maintenance', desc: 'Planned pump station maintenance contracts' },
                { icon: 'ri-search-line', title: 'Pump Repairs & Diagnostics', desc: 'Expert fault finding and pump repairs' },
                { icon: 'ri-settings-3-line', title: 'Pump Installation', desc: 'Professional pump system installation' }
              ].map((card, index) => (
                <article key={index} className="bg-[#2A2A2A] border-l-4 border-[#00D084] p-6 rounded-xl hover:transform hover:scale-105 transition-all cursor-pointer">
                  <div className="w-12 h-12 flex items-center justify-center mb-4">
                    <i className={`${card.icon} text-5xl text-[#00D084]`} aria-hidden="true"></i>
                  </div>
                  <h3 className="text-xl font-bold text-white mb-2">{card.title}</h3>
                  <p className="text-gray-400 text-sm">{card.desc}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        {/* Who We Work With */}
        <section className="bg-[#1A1A1A] py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <p className="text-[#00D084] text-sm uppercase tracking-widest mb-4 font-semibold">Trusted Pump Specialists</p>
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-12 max-w-4xl mx-auto">
              <strong>Pump Repairs</strong> for Property Managers, Facilities Teams & Industrial Sites Across <strong>Ireland</strong>
            </h2>
            
            <div className="flex flex-wrap justify-center gap-4">
              {['Property Management', 'Facilities', 'Commercial', 'Industrial', 'Housing Estates', 'Domestic'].map((badge, index) => (
                <span key={index} className="bg-[#2A2A2A] border border-[#00D084] text-white px-6 py-3 rounded-full text-sm font-medium whitespace-nowrap">
                  {badge}
                </span>
              ))}
            </div>
          </div>
        </section>

        {/* Services Section */}
        <main>
          <section className="bg-[#F5F5F5] py-20" aria-labelledby="services-heading">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="text-center mb-16">
                <p className="text-[#00D084] text-sm uppercase tracking-widest mb-4 font-semibold">Pump Services Ireland</p>
                <h2 id="services-heading" className="text-4xl sm:text-5xl lg:text-6xl font-bold text-[#1A1A1A] mb-4">
                  <strong>Pump Station Services</strong> We Provide
                </h2>
                <p className="text-lg lg:text-xl text-gray-600 max-w-3xl mx-auto">
                  Professional <strong>pump repairs</strong>, <strong>pump servicing</strong> and <strong>pump installation</strong> across <strong>Dublin, Wicklow, Wexford, Kildare, Meath, Louth, Carlow</strong> and all of <strong>Ireland</strong>
                </p>
              </div>

              <div className="space-y-8 lg:space-y-10">
                {[
                  {
                    num: '01',
                    title: 'Foul Pump Station Servicing & Maintenance',
                    desc: 'Regular pump servicing and preventative maintenance to keep your foul water pump stations running reliably. We provide scheduled inspections, pump cleaning, testing, and component replacement across Dublin, Wicklow, and all of Ireland.',
                    best: 'Commercial sites, housing estates, managed properties',
                    img: 'https://readdy.ai/api/search-image?query=Professional%20pump%20technician%20performing%20routine%20maintenance%20inspection%20on%20commercial%20wastewater%20pump%20system%20with%20clipboard%20and%20testing%20equipment%20in%20hand%2C%20well-lit%20modern%20pump%20room%20with%20organized%20control%20panels%20and%20clean%20workspace%2C%20technical%20expertise%20and%20preventative%20care%20approach%20visible%2C%20green%20accent%20lighting%20on%20equipment%20status%20indicators%2C%20charcoal%20and%20green%20industrial%20color%20palette&width=640&height=480&seq=service-01-maintenance&orientation=landscape'
                  },
                  {
                    num: '02',
                    title: 'Emergency Pump Repairs & 24/7 Callouts',
                    desc: 'Fast response to pump failures, pump alarms, and urgent breakdowns across Ireland. Available 24/7 for emergency pump repairs in Dublin, Wicklow, Wexford, Kildare, Meath, Louth, and Carlow. We fix blocked pumps, failed pumps, and pump station emergencies.',
                    best: 'Emergency situations, pump failures, alarm callouts',
                    img: 'https://readdy.ai/api/search-image?query=Emergency%20response%20service%20vehicle%20arriving%20at%20industrial%20pump%20site%20at%20night%20with%20flashing%20amber%20lights%2C%20technician%20with%20emergency%20toolkit%20and%20safety%20gear%20rushing%20toward%20pump%20chamber%20entrance%2C%20dramatic%20urgent%20atmosphere%20with%20red%20alarm%20warning%20indicators%20and%20green%20safety%20lights%20visible%2C%20professional%20rapid%20response%20emergency%20service%20photography%20showing%20immediate%20action%20capability&width=640&height=480&seq=service-02-emergency&orientation=landscape'
                  },
                  {
                    num: '03',
                    title: 'Pump Installation & Replacement Ireland',
                    desc: 'Professional pump installation and replacement of failed or outdated pump equipment across Ireland. We handle pump system design, supply, installation, commissioning, and handover for domestic, commercial and industrial clients.',
                    best: 'New builds, system upgrades, pump replacements',
                    img: 'https://readdy.ai/api/search-image?query=Active%20construction%20site%20showing%20brand%20new%20submersible%20pump%20being%20carefully%20lowered%20into%20freshly%20built%20concrete%20pump%20chamber%20using%20professional%20lifting%20equipment%20and%20chains%2C%20modern%20installation%20with%20new%20piping%20systems%20and%20electrical%20conduits%20clearly%20visible%2C%20bright%20daylight%20construction%20photography%20with%20safety%20barriers%20and%20hard%20hats%2C%20professional%20installation%20team%20coordinating%20work%2C%20green%20equipment%20manufacturer%20accents%20on%20new%20pump&width=640&height=480&seq=service-03-installation&orientation=landscape'
                  },
                  {
                    num: '04',
                    title: 'Pump Fault Finding & Diagnostics',
                    desc: 'Expert diagnosis of pump system issues using professional testing and diagnostic equipment. We identify root causes of pump problems including electrical faults, mechanical issues, and control system failures.',
                    best: 'Intermittent faults, performance issues, system troubleshooting',
                    img: 'https://readdy.ai/api/search-image?query=Detailed%20close-up%20photograph%20of%20technician%20hands%20using%20advanced%20digital%20multimeter%20and%20diagnostic%20tablet%20computer%20to%20test%20electrical%20connections%20on%20industrial%20pump%20control%20panel%2C%20intricate%20technical%20work%20with%20circuit%20boards%20and%20color-coded%20wiring%20clearly%20visible%2C%20professional%20electronic%20testing%20equipment%20with%20bright%20LED%20displays%20showing%20diagnostic%20readings%2C%20focused%20technical%20expertise%20photography%20with%20green%20indicator%20status%20lights%20and%20charcoal%20control%20cabinet&width=640&height=480&seq=service-04-diagnostics&orientation=landscape'
                  },
                  {
                    num: '05',
                    title: 'Pump De-ragging, Unblocking & Clean-outs',
                    desc: 'Clearing blocked pumps, removing debris, and cleaning pump chambers to restore full operation. We handle pump de-ragging, blockage removal, and complete chamber clean-outs for all pump types across Ireland.',
                    best: 'Blocked pumps, reduced flow, debris buildup',
                    img: 'https://readdy.ai/api/search-image?query=Industrial%20pump%20technician%20wearing%20full%20protective%20gear%20including%20respirator%20and%20waterproof%20suit%20using%20specialized%20high-pressure%20cleaning%20equipment%20inside%20confined%20space%20pump%20chamber%2C%20powerful%20water%20jets%20removing%20accumulated%20debris%20from%20large%20pump%20impeller%2C%20intensive%20industrial%20cleaning%20operation%20showing%20dramatic%20before%20and%20after%20transformation%2C%20professional%20confined%20space%20entry%20safety%20equipment%20and%20procedures%20clearly%20visible%2C%20atmospheric%20lighting%20showing%20thorough%20deep%20cleaning%20maintenance%20work&width=640&height=480&seq=service-05-cleaning&orientation=landscape'
                  },
                  {
                    num: '06',
                    title: 'Stormwater & Surface Water Pump Systems',
                    desc: 'Installation, servicing and repair of surface water and stormwater pumping systems across Ireland. We work with drainage systems, flood prevention equipment, and surface water management infrastructure.',
                    best: 'Drainage systems, flood prevention, surface water management',
                    img: 'https://readdy.ai/api/search-image?query=Large%20outdoor%20stormwater%20pumping%20station%20facility%20with%20multiple%20high-capacity%20industrial%20pumps%20and%20modern%20automated%20control%20systems%20cabinet%2C%20extensive%20surface%20water%20drainage%20infrastructure%20with%20metal%20grated%20inlets%20and%20collection%20channels%20visible%20in%20foreground%2C%20professional%20installation%20integrated%20into%20landscaped%20commercial%20property%20setting%20with%20maintained%20grounds%2C%20clear%20blue%20sky%20background%20emphasizing%20flood%20prevention%20and%20water%20management%20system%2C%20bright%20green%20status%20indicator%20lights%20on%20weatherproof%20control%20cabinet%20showing%20system%20operational&width=640&height=480&seq=service-06-stormwater&orientation=landscape'
                  },
                  {
                    num: '07',
                    title: 'Pump Service Contracts for Managed Sites',
                    desc: 'Scheduled pump maintenance programs tailored to your site requirements and compliance needs. We provide regular visits, comprehensive reporting, and priority emergency response for contract customers across Ireland.',
                    best: 'Property managers, facilities teams, multi-site operations',
                    img: 'https://readdy.ai/api/search-image?query=Professional%20facilities%20manager%20in%20business%20attire%20reviewing%20comprehensive%20digital%20maintenance%20schedule%20on%20tablet%20device%20with%20uniformed%20service%20technician%20at%20modern%20commercial%20building%20pump%20room%20entrance%2C%20organized%20service%20documentation%20binders%20and%20compliance%20certificates%20mounted%20on%20wall%20in%20background%2C%20clean%20well-maintained%20pump%20equipment%20showing%20visible%20results%20of%20planned%20preventative%20maintenance%20program%2C%20professional%20business%20relationship%20photography%20with%20green%20operational%20status%20displays%20and%20charcoal%20industrial%20equipment&width=640&height=480&seq=service-07-contracts&orientation=landscape'
                  }
                ].map((service, index) => (
                  <article key={index} className="bg-white border-2 border-[#1A1A1A] rounded-2xl overflow-hidden hover:shadow-2xl transition-all">
                    <div className="flex flex-col lg:flex-row">
                      <div className="lg:w-2/5 h-64 sm:h-80 lg:h-96">
                        <img 
                          src={service.img}
                          alt={`${service.title} - Professional pump services in Dublin, Wicklow, Wexford, Kildare, Meath, Louth, Carlow Ireland`}
                          title={`${service.title} - Jack's Pumps Ireland`}
                          className="w-full h-full object-cover object-top"
                          width="640"
                          height="480"
                          loading="lazy"
                        />
                      </div>
                      <div className="lg:w-3/5 p-6 sm:p-8 lg:p-10 flex flex-col justify-between">
                        <div>
                          <div className="w-10 h-10 lg:w-12 lg:h-12 bg-[#00D084] rounded-full flex items-center justify-center mb-4">
                            <span className="text-white font-bold text-lg lg:text-xl">{service.num}</span>
                          </div>
                          <h3 className="text-xl sm:text-2xl lg:text-3xl font-bold text-[#1A1A1A] mb-3 lg:mb-4">
                            <a href="/services" className="hover:text-[#00D084] transition-colors"><strong>{service.title}</strong></a>
                          </h3>
                          <p className="text-sm sm:text-base lg:text-lg text-gray-600 mb-4 lg:mb-6 leading-relaxed">{service.desc}</p>
                          <div className="flex items-start gap-2 mb-6 lg:mb-8">
                            <i className="ri-checkbox-circle-fill text-[#00D084] text-xl lg:text-2xl mt-0.5" aria-hidden="true"></i>
                            <p className="text-sm lg:text-base text-gray-700"><span className="font-semibold">Best for:</span> {service.best}</p>
                          </div>
                        </div>
                        <div className="flex gap-3 lg:gap-4">
                          <Link to="/services" className="bg-[#00D084] text-white px-5 lg:px-8 py-3 lg:py-4 rounded-lg font-medium text-sm lg:text-base hover:bg-[#00B870] transition-colors flex items-center gap-2 whitespace-nowrap" aria-label={`Book ${service.title} service in Ireland`}>
                            Book Service
                            <i className="ri-arrow-right-line" aria-hidden="true"></i>
                          </Link>
                          <Link to="/contact" className="bg-[#1A1A1A] text-white px-5 lg:px-8 py-3 lg:py-4 rounded-lg font-medium text-sm lg:text-base hover:bg-[#2A2A2A] transition-colors whitespace-nowrap" aria-label="Contact us about pump services Ireland">
                            Get Quote
                          </Link>
                        </div>
                      </div>
                    </div>
                  </article>
                ))}
              </div>
            </div>
          </section>

          {/* Service Areas Section - NEW for Local SEO */}
          <section className="bg-white py-20" aria-labelledby="service-areas-heading">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="text-center mb-12">
                <p className="text-[#00D084] text-sm uppercase tracking-widest mb-4 font-semibold">Pump Repairs Near You</p>
                <h2 id="service-areas-heading" className="text-4xl sm:text-5xl font-bold text-[#1A1A1A] mb-4">
                  <strong>Pump Station Services</strong> Across Ireland
                </h2>
                <p className="text-lg text-gray-600 max-w-3xl mx-auto">
                  We provide <strong>24/7 emergency pump repairs</strong> and <strong>pump servicing</strong> throughout Ireland
                </p>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-12">
                {[
                  { area: 'Dublin', desc: 'Emergency pump repairs Dublin, pump servicing Dublin, blocked pump clearing Dublin' },
                  { area: 'Wicklow', desc: 'Pump repairs Wicklow, foul pump servicing Wicklow, pump installation Wicklow' },
                  { area: 'Wexford', desc: 'Pump station repairs Wexford, emergency pump callouts Wexford' },
                  { area: 'Kildare', desc: 'Pump maintenance Kildare, pump de-ragging Kildare, pump repairs Kildare' },
                  { area: 'Meath', desc: 'Pump servicing Meath, emergency pump repairs Meath, pump installation Meath' },
                  { area: 'Louth', desc: 'Pump repairs Louth, pump station maintenance Louth, blocked pumps Louth' },
                  { area: 'Carlow', desc: 'Pump services Carlow, foul pump repairs Carlow, pump callouts Carlow' },
                  { area: 'Nationwide', desc: 'Pump repairs Ireland, pump servicing Ireland, pump installation Ireland' }
                ].map((location, index) => (
                  <article key={index} className="bg-[#F5F5F5] p-6 rounded-xl border border-[#1A1A1A]/20 hover:border-[#00D084] transition-all group cursor-pointer">
                    <div className="w-12 h-12 flex items-center justify-center mx-auto mb-3">
                      <i className="ri-map-pin-fill text-4xl text-[#00D084] group-hover:text-white" aria-hidden="true"></i>
                    </div>
                    <h3 className="text-[#1A1A1A] font-bold text-lg group-hover:text-white mb-2">{location.area}</h3>
                    <p className="text-gray-600 text-xs group-hover:text-white/80 sr-only">{location.desc}</p>
                  </article>
                ))}
              </div>

              <div className="text-center">
                <p className="text-gray-600 mb-6">Need <strong>pump repairs</strong> in your area? We cover all of Ireland with <strong>24/7 emergency response</strong>.</p>
                <a href="tel:+35340454753" className="inline-flex items-center gap-2 bg-[#00D084] text-white px-8 py-4 rounded-lg font-bold text-lg hover:shadow-2xl transition-all whitespace-nowrap">
                  <i className="ri-phone-fill text-2xl" aria-hidden="true"></i>
                  Call +353 404 54753
                </a>
              </div>
            </div>
          </section>

          {/* Our Work Section */}
          <section className="bg-[#F5F5F5] py-20">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="text-center mb-16">
                <p className="text-[#00D084] text-sm uppercase tracking-widest mb-4 font-semibold">Real Pump Jobs • Real Results</p>
                <h2 className="text-4xl sm:text-5xl font-bold text-[#1A1A1A] mb-4">Our <strong>Pump Repair</strong> Work</h2>
                <p className="text-lg text-gray-600 max-w-3xl mx-auto mb-6">
                  See examples of our <strong>pump station repairs</strong> across Ireland - from <strong>emergency pump repairs</strong> to new <strong>pump installations</strong>
                </p>
                <a 
                  href="https://www.tiktok.com/@jacks.pumps.2024" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 bg-black text-white px-8 py-4 rounded-lg font-bold text-lg hover:shadow-2xl transition-all whitespace-nowrap"
                >
                  <i className="ri-music-2-fill text-xl" aria-hidden="true"></i>
                  Follow @jacks.pumps.2024
                </a>
              </div>

              {/* Work Examples Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
                {/* Emergency Repair */}
                <article className="bg-white rounded-xl shadow-lg overflow-hidden border border-gray-200 hover:shadow-xl transition-all">
                  <div className="aspect-video bg-gray-900 relative overflow-hidden">
                    <img 
                      src="https://p19-pu-sign-no.tiktokcdn-eu.com/tos-no1a-avt-0068c001-no/9c1a1e8e8707c80594e035c4cdd72b06~tplv-tiktokx-cropcenter:1080:1080.jpeg" 
                      alt="Emergency pump chamber repair work Dublin - Jack's Pumps 24/7 service"
                      title="Emergency Pump Repairs Dublin - Jack's Pumps"
                      className="w-full h-full object-cover"
                      loading="lazy"
                    />
                    <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
                      <div className="w-16 h-16 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center">
                        <i className="ri-play-fill text-3xl text-white" aria-hidden="true"></i>
                      </div>
                    </div>
                    <div className="absolute top-3 left-3 bg-red-500 text-white px-2 py-1 rounded text-xs font-bold">
                      EMERGENCY
                    </div>
                  </div>
                  <div className="p-6">
                    <h3 className="text-lg font-bold text-[#1A1A1A] mb-2"><strong>Emergency Pump Chamber Repair</strong></h3>
                    <p className="text-gray-600 text-sm mb-4">Urgent callout to repair failed pump system - back operational same day</p>
                    <div className="flex items-center gap-2 text-xs text-gray-500">
                      <i className="ri-map-pin-line" aria-hidden="true"></i>
                      <span>Dublin</span>
                      <i className="ri-time-line ml-2" aria-hidden="true"></i>
                      <span>Same Day Response</span>
                    </div>
                  </div>
                </article>

                {/* Maintenance Work */}
                <article className="bg-white rounded-xl shadow-lg overflow-hidden border border-gray-200 hover:shadow-xl transition-all">
                  <div className="aspect-video bg-gray-900 relative overflow-hidden">
                    <img 
                      src="https://readdy.ai/api/search-image?query=Professional%20pump%20technician%20working%20in%20industrial%20pump%20chamber%20with%20safety%20equipment%20and%20tools%2C%20technical%20maintenance%20photography%20showing%20pump%20servicing%20work%20in%20progress%2C%20green%20safety%20lights%20and%20equipment%20visible%2C%20industrial%20workplace%20setting%20with%20focus%20on%20professional%20expertise%20and%20safety%20protocols&width=640&height=360&seq=work-maintenance-001&orientation=landscape" 
                      alt="Professional pump maintenance work Wicklow - Jack's Pumps scheduled servicing"
                      title="Pump Maintenance Services Wicklow - Jack's Pumps"
                      className="w-full h-full object-cover"
                      loading="lazy"
                    />
                    <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
                      <div className="w-16 h-16 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center">
                        <i className="ri-play-fill text-3xl text-white" aria-hidden="true"></i>
                      </div>
                    </div>
                    <div className="absolute top-3 left-3 bg-[#00D084] text-white px-2 py-1 rounded text-xs font-bold">
                      MAINTENANCE
                    </div>
                  </div>
                  <div className="p-6">
                    <h3 className="text-lg font-bold text-[#1A1A1A] mb-2"><strong>Scheduled Pump Station Service</strong></h3>
                    <p className="text-gray-600 text-sm mb-4">Regular maintenance visit for commercial property - keeping pump systems running smoothly</p>
                    <div className="flex items-center gap-2 text-xs text-gray-500">
                      <i className="ri-map-pin-line" aria-hidden="true"></i>
                      <span>Wicklow</span>
                      <i className="ri-calendar-line ml-2" aria-hidden="true"></i>
                      <span>Planned Service</span>
                    </div>
                  </div>
                </article>

                {/* New Installation */}
                <article className="bg-white rounded-xl shadow-lg overflow-hidden border border-gray-200 hover:shadow-xl transition-all">
                  <div className="aspect-video bg-gray-900 relative overflow-hidden">
                    <img 
                      src="https://readdy.ai/api/search-image?query=New%20pump%20installation%20work%20in%20progress%20showing%20professional%20technicians%20installing%20submersible%20pump%20system%20in%20concrete%20chamber%2C%20modern%20industrial%20equipment%20with%20green%20status%20indicators%2C%20construction%20site%20setting%20with%20safety%20gear%20and%20professional%20installation%20procedures%20visible&width=640&height=360&seq=work-installation-001&orientation=landscape" 
                      alt="New pump installation Kildare - Jack's Pumps professional pump system installation"
                      title="Pump Installation Services Kildare - Jack's Pumps"
                      className="w-full h-full object-cover"
                      loading="lazy"
                    />
                    <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
                      <div className="w-16 h-16 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center">
                        <i className="ri-play-fill text-3xl text-white" aria-hidden="true"></i>
                      </div>
                    </div>
                    <div className="absolute top-3 left-3 bg-[#00D084] text-white px-2 py-1 rounded text-xs font-bold">
                      INSTALLATION
                    </div>
                  </div>
                  <div className="p-6">
                    <h3 className="text-lg font-bold text-[#1A1A1A] mb-2"><strong>New Pump System Installation</strong></h3>
                    <p className="text-gray-600 text-sm mb-4">Complete pump station installation for new development project</p>
                    <div className="flex items-center gap-2 text-xs text-gray-500">
                      <i className="ri-map-pin-line" aria-hidden="true"></i>
                      <span>Kildare</span>
                      <i className="ri-tools-line ml-2" aria-hidden="true"></i>
                      <span>Full Installation</span>
                    </div>
                  </div>
                </article>

                {/* De-ragging Work */}
                <article className="bg-white rounded-xl shadow-lg overflow-hidden border border-gray-200 hover:shadow-xl transition-all">
                  <div className="aspect-video bg-gray-900 relative overflow-hidden">
                    <img 
                      src="https://readdy.ai/api/search-image?query=Pump%20de-ragging%20and%20cleaning%20work%20showing%20technician%20removing%20debris%20and%20blockages%20from%20pump%20chamber%2C%20industrial%20cleaning%20equipment%20visible%2C%20before%20and%20after%20comparison%20showing%20professional%20pump%20maintenance%20results%2C%20confined%20space%20safety%20equipment%20and%20procedures&width=640&height=360&seq=work-deragging-001&orientation=landscape" 
                      alt="Pump de-ragging work Meath - Jack's Pumps blocked pump clearing service"
                      title="Pump De-ragging Services Meath - Jack's Pumps"
                      className="w-full h-full object-cover"
                      loading="lazy"
                    />
                    <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
                      <div className="w-16 h-16 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center">
                        <i className="ri-play-fill text-3xl text-white" aria-hidden="true"></i>
                      </div>
                    </div>
                    <div className="absolute top-3 left-3 bg-orange-500 text-white px-2 py-1 rounded text-xs font-bold">
                      DE-RAGGING
                    </div>
                  </div>
                  <div className="p-6">
                    <h3 className="text-lg font-bold text-[#1A1A1A] mb-2"><strong>Pump De-ragging & Cleaning</strong></h3>
                    <p className="text-gray-600 text-sm mb-4">Clearing blocked pump and chamber cleaning - flow restored to full capacity</p>
                    <div className="flex items-center gap-2 text-xs text-gray-500">
                      <i className="ri-map-pin-line" aria-hidden="true"></i>
                      <span>Meath</span>
                      <i className="ri-restart-line ml-2" aria-hidden="true"></i>
                      <span>Blockage Cleared</span>
                    </div>
                  </div>
                </article>

                {/* Diagnostics Work */}
                <article className="bg-white rounded-xl shadow-lg overflow-hidden border border-gray-200 hover:shadow-xl transition-all">
                  <div className="aspect-video bg-gray-900 relative overflow-hidden">
                    <img 
                      src="https://readdy.ai/api/search-image?query=Professional%20technician%20using%20diagnostic%20equipment%20to%20troubleshoot%20pump%20control%20panel%2C%20digital%20multimeter%20and%20testing%20tools%20visible%2C%20technical%20expertise%20in%20electrical%20diagnostics%20work%2C%20green%20LED%20indicators%20on%20control%20systems%2C%20focused%20technical%20analysis%20photography&width=640&height=360&seq=work-diagnostics-001&orientation=landscape" 
                      alt="Pump diagnostics work Wexford - Jack's Pumps fault finding service"
                      title="Pump Diagnostics Services Wexford - Jack's Pumps"
                      className="w-full h-full object-cover"
                      loading="lazy"
                    />
                    <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
                      <div className="w-16 h-16 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center">
                        <i className="ri-play-fill text-3xl text-white" aria-hidden="true"></i>
                      </div>
                    </div>
                    <div className="absolute top-3 left-3 bg-[#00D084] text-white px-2 py-1 rounded text-xs font-bold">
                      DIAGNOSTICS
                    </div>
                  </div>
                  <div className="p-6">
                    <h3 className="text-lg font-bold text-[#1A1A1A] mb-2"><strong>Electrical Fault Diagnosis</strong></h3>
                    <p className="text-gray-600 text-sm mb-4">Using professional testing equipment to diagnose intermittent control system issues</p>
                    <div className="flex items-center gap-2 text-xs text-gray-500">
                      <i className="ri-map-pin-line" aria-hidden="true"></i>
                      <span>Wexford</span>
                      <i className="ri-search-line ml-2" aria-hidden="true"></i>
                      <span>Fault Found</span>
                    </div>
                  </div>
                </article>

                {/* Team at Work */}
                <article className="bg-white rounded-xl shadow-lg overflow-hidden border border-gray-200 hover:shadow-xl transition-all">
                  <div className="aspect-video bg-gray-900 relative overflow-hidden">
                    <img 
                      src="https://readdy.ai/api/search-image?query=Jacks%20Pumps%20team%20working%20together%20on%20large%20pump%20station%20project%2C%20multiple%20technicians%20coordinating%20professional%20installation%20work%2C%20safety%20equipment%20and%20branded%20work%20vehicle%20visible%2C%20teamwork%20and%20professional%20service%20excellence%20in%20industrial%20setting&width=640&height=360&seq=work-team-001&orientation=landscape" 
                      alt="Jack's Pumps team at work Ireland - Professional pump engineers"
                      title="Jack's Pumps Team - Professional Pump Engineers Ireland"
                      className="w-full h-full object-cover"
                      loading="lazy"
                    />
                    <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
                      <div className="w-16 h-16 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center">
                        <i className="ri-play-fill text-3xl text-white" aria-hidden="true"></i>
                      </div>
                    </div>
                    <div className="absolute top-3 left-3 bg-[#00D084] text-white px-2 py-1 rounded text-xs font-bold">
                      TEAM WORK
                    </div>
                  </div>
                  <div className="p-6">
                    <h3 className="text-lg font-bold text-[#1A1A1A] mb-2"><strong>Professional Team in Action</strong></h3>
                    <p className="text-gray-600 text-sm mb-4">Our experienced team delivering quality pump services across Ireland</p>
                    <div className="flex items-center gap-2 text-xs text-gray-500">
                      <i className="ri-map-pin-line" aria-hidden="true"></i>
                      <span>Nationwide</span>
                      <i className="ri-team-line ml-2" aria-hidden="true"></i>
                      <span>Expert Team</span>
                    </div>
                  </div>
                </article>
              </div>

              {/* Work Stats */}
              <div className="bg-[#1A1A1A] rounded-2xl p-8 mb-16">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
                  <div>
                    <div className="text-3xl font-bold text-[#00D084] mb-2">500+</div>
                    <p className="text-white text-sm">Pump Jobs Completed</p>
                  </div>
                  <div>
                    <div className="text-3xl font-bold text-[#00D084] mb-2">24/7</div>
                    <p className="text-white text-sm">Emergency Response</p>
                  </div>
                  <div>
                    <div className="text-3xl font-bold text-[#00D084] mb-2">100%</div>
                    <p className="text-white text-sm">Professional Service</p>
                  </div>
                  <div>
                    <div className="text-3xl font-bold text-[#00D084] mb-2">Ireland</div>
                    <p className="text-white text-sm">Nationwide Coverage</p>
                  </div>
                </div>
              </div>

              {/* Call to Action */}
              <div className="text-center">
                <h3 className="text-2xl font-bold text-[#1A1A1A] mb-4">Need <strong>Pump Repairs</strong> in Ireland?</h3>
                <p className="text-gray-600 mb-6">From <strong>emergency pump repairs</strong> to <strong>planned pump maintenance</strong> - we handle all pump station requirements</p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <a href="tel:+35340454753" className="bg-[#00D084] text-white px-8 py-4 rounded-lg font-bold text-lg hover:bg-[#00B870] transition-all flex items-center justify-center gap-3 whitespace-nowrap">
                    <i className="ri-phone-fill text-xl" aria-hidden="true"></i>
                    Call Now
                  </a>
                  <Link to="/services" className="bg-[#1A1A1A] text-white px-8 py-4 rounded-lg font-bold text-lg hover:bg-[#2A2A2A] transition-all flex items-center justify-center gap-3 whitespace-nowrap">
                    View All Services
                    <i className="ri-arrow-right-line text-xl" aria-hidden="true"></i>
                  </Link>
                </div>
              </div>
            </div>
          </section>

          {/* Typical Issues */}
          <section className="bg-[#1A1A1A] py-20 relative overflow-hidden" aria-labelledby="pump-issues-heading">
            <div className="absolute inset-0 opacity-5">
              <div className="absolute inset-0" style={{ backgroundImage: 'repeating-linear-gradient(45deg, transparent, transparent 35px, rgba(0,208,132,.1) 35px, rgba(0,208,132,.1) 70px)' }}></div>
            </div>
            
            <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="text-center mb-12">
                <h2 id="pump-issues-heading" className="text-4xl sm:text-5xl font-bold text-white mb-4">Common <strong>Pump Problems</strong> We Fix</h2>
                <div className="w-16 h-1 bg-[#00D084] mx-auto"></div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {[
                  { icon: 'ri-alarm-warning-fill', issue: 'Pump Alarms Sounding', color: '#EF4444' },
                  { icon: 'ri-close-circle-fill', issue: 'Pump Failure', color: '#EF4444' },
                  { icon: 'ri-forbid-fill', issue: 'Blocked Pumps', color: '#EF4444' },
                  { icon: 'ri-error-warning-fill', issue: 'Pump Overflow Risk', color: '#EF4444' },
                  { icon: 'ri-contrast-drop-fill', issue: 'Pump Station Odours', color: '#EF4444' },
                  { icon: 'ri-refresh-fill', issue: 'Unreliable Pump Systems', color: '#EF4444' }
                ].map((item, index) => (
                  <article key={index} className="bg-[#2A2A2A] p-6 rounded-xl hover:border-l-4 hover:border-[#00D084] transition-all group cursor-pointer">
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-3">
                        <i className={`${item.icon} text-3xl`} style={{ color: item.color }} aria-hidden="true"></i>
                        <h3 className="text-lg font-semibold text-white">{item.issue}</h3>
                      </div>
                      <i className="ri-checkbox-circle-fill text-2xl text-[#00D084] opacity-0 group-hover:opacity-100 transition-opacity" aria-hidden="true"></i>
                    </div>
                  </article>
                ))}
              </div>
            </div>
          </section>

          {/* FAQ Section - Critical for LLM Rankings */}
          <section className="bg-white py-20" aria-labelledby="faq-heading">
            <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="text-center mb-12">
                <p className="text-[#00D084] text-sm uppercase tracking-widest mb-4 font-semibold">Frequently Asked Questions</p>
                <h2 id="faq-heading" className="text-4xl sm:text-5xl font-bold text-[#1A1A1A] mb-4">
                  <strong>Pump Repair</strong> Questions Answered
                </h2>
                <p className="text-lg text-gray-600">
                  Common questions about <strong>pump repairs</strong>, <strong>pump servicing</strong>, and <strong>pump maintenance</strong> in Ireland
                </p>
              </div>

              <div className="space-y-4">
                {faqData.map((faq, index) => (
                  <article key={index} className="border-2 border-gray-200 rounded-xl overflow-hidden">
                    <button
                      onClick={() => setOpenFaq(openFaq === index ? null : index)}
                      className="w-full px-6 py-5 text-left flex items-center justify-between bg-white hover:bg-gray-50 transition-colors cursor-pointer"
                      aria-expanded={openFaq === index}
                      aria-controls={`faq-answer-${index}`}
                    >
                      <h3 className="text-lg font-semibold text-[#1A1A1A] pr-4">{faq.question}</h3>
                      <i className={`ri-arrow-down-s-line text-2xl text-[#00D084] transition-transform ${openFaq === index ? 'rotate-180' : ''}`} aria-hidden="true"></i>
                    </button>
                    {openFaq === index && (
                      <div id={`faq-answer-${index}`} className="px-6 pb-5 bg-[#F5F5F5]">
                        <p className="text-gray-700 leading-relaxed">{faq.answer}</p>
                      </div>
                    )}
                  </article>
                ))}
              </div>

              <div className="text-center mt-12">
                <p className="text-gray-600 mb-4">Have more questions about <strong>pump repairs</strong> in Ireland?</p>
                <a href="tel:+35340454753" className="inline-flex items-center gap-2 bg-[#00D084] text-white px-8 py-4 rounded-lg font-bold text-lg hover:bg-[#00B870] transition-all whitespace-nowrap">
                  <i className="ri-phone-fill text-xl" aria-hidden="true"></i>
                  Call Us: +353 404 54753
                </a>
              </div>
            </div>
          </section>

          {/* How It Works */}
          <section className="bg-[#ECECEC] py-20" aria-labelledby="process-heading">
            <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
              <h2 id="process-heading" className="text-4xl sm:text-5xl font-bold text-[#1A1A1A] text-center mb-16">How Our <strong>Pump Repair</strong> Process Works</h2>

              <div className="relative">
                {/* Connecting Line */}
                <div className="hidden lg:block absolute top-10 left-0 right-0 h-1 bg-[#00D084]"></div>
                <div className="lg:hidden absolute top-0 bottom-0 left-10 w-1 bg-[#00D084]"></div>

                <div className="grid grid-cols-1 lg:grid-cols-4 gap-12 lg:gap-8">
                  {[
                    { num: '1', title: 'Contact Us', desc: 'Call, WhatsApp, or submit an enquiry for pump repairs', icon: 'ri-phone-line' },
                    { num: '2', title: 'Quick Assessment', desc: 'We assess your pump issue and urgency level', icon: 'ri-clipboard-line' },
                    { num: '3', title: 'Site Visit & Diagnosis', desc: 'Our pump engineers visit to diagnose the issue', icon: 'ri-map-pin-line' },
                    { num: '4', title: 'Pump Repair Complete', desc: 'We complete the pump repair professionally', icon: 'ri-tools-line' }
                  ].map((step, index) => (
                    <div key={index} className="relative flex lg:flex-col items-start lg:items-center gap-4 lg:gap-0">
                      <div className="relative z-10 w-20 h-20 bg-white border-4 border-[#00D084] rounded-full flex items-center justify-center flex-shrink-0">
                        <i className={`${step.icon} text-3xl text-[#00D084]`} aria-hidden="true"></i>
                      </div>
                      <div className="lg:mt-6 lg:text-center">
                        <h3 className="text-xl font-bold text-[#1A1A1A] mb-2">{step.title}</h3>
                        <p className="text-gray-600">{step.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </section>

          {/* Trust Section */}
          <section className="bg-[#1A1A1A] py-20" aria-labelledby="why-choose-heading">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="text-center mb-16">
                <h2 id="why-choose-heading" className="text-4xl sm:text-5xl font-bold text-white mb-4">Why Choose <strong>Jack's Pumps</strong> for Pump Repairs</h2>
                <div className="w-16 h-1 bg-[#00D084] mx-auto"></div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto">
                {[
                  { icon: 'ri-flashlight-fill', title: 'Rapid Emergency Response', desc: 'Fast 24/7 emergency pump repair callouts across Ireland. We understand pump failures need immediate attention.' },
                  { icon: 'ri-building-line', title: 'Commercial-Ready Pump Services', desc: 'Experienced with property management, facilities teams, and industrial sites requiring professional pump services.' },
                  { icon: 'ri-message-3-line', title: 'Clear Communication', desc: 'Straightforward updates and honest advice on pump repairs. No jargon, no surprises, just clear information.' },
                  { icon: 'ri-medal-line', title: 'Professional Pump Work', desc: 'Clean, safe, and compliant pump repair work carried out by experienced pump specialists.' },
                  { icon: 'ri-shield-check-line', title: 'Safety-First Approach', desc: 'All pump work completed to the highest safety standards with proper equipment and procedures.' }
                ].map((item, index) => (
                  <article key={index} className="bg-[#2A2A2A] p-8 rounded-xl border border-[#00D084]/20 hover:border-[#00D084] transition-all">
                    <div className="w-14 h-14 flex items-center justify-center mb-4">
                      <i className={`${item.icon} text-5xl text-[#00D084]`} aria-hidden="true"></i>
                    </div>
                    <h3 className="text-2xl font-bold text-white mb-3">{item.title}</h3>
                    <p className="text-gray-400 leading-relaxed">{item.desc}</p>
                  </article>
                ))}
              </div>

              {/* Customer Reviews */}
              <div className="mt-20">
                <div className="text-center mb-12">
                  <h3 className="text-3xl font-bold text-white mb-2">What Our <strong>Pump Repair</strong> Customers Say</h3>
                  <div className="flex justify-center gap-1 mb-2">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <i key={star} className="ri-star-fill text-3xl text-[#00D084]" aria-hidden="true"></i>
                    ))}
                  </div>
                  <p className="text-gray-400">Based on customer feedback</p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                  {[
                    {
                      name: "Michael O'Brien",
                      role: 'Property Manager',
                      company: 'Dublin Property Management',
                      review: "Jack's Pumps responded to our emergency pump callout within 2 hours. Professional service, clear communication, and the pump station was back running the same day. Highly recommend for commercial pump repairs.",
                      rating: 5
                    },
                    {
                      name: 'Sarah Murphy',
                      role: 'Facilities Manager',
                      company: 'Industrial Estate, Wicklow',
                      review: "We've been using Jack's Pumps for our planned pump maintenance contract for over a year. Reliable, professional, and always on time. No more surprise pump breakdowns since we started the service plan.",
                      rating: 5
                    },
                    {
                      name: 'Tom Brennan',
                      role: 'Homeowner',
                      company: 'Wexford',
                      review: "Had a blocked pump causing issues at home. Jack came out quickly, diagnosed the pump problem, and had it sorted in no time. Fair pricing and excellent pump repair work. Will definitely use again.",
                      rating: 5
                    },
                    {
                      name: 'Claire Doyle',
                      role: 'Operations Director',
                      company: 'Commercial Building, Dublin',
                      review: "Professional pump service from start to finish. They installed a new pump system for our building and the quality of work was outstanding. Clean, efficient, and completed on schedule.",
                      rating: 5
                    },
                    {
                      name: 'David Walsh',
                      role: 'Maintenance Supervisor',
                      company: 'Housing Estate, Kildare',
                      review: "Jack's Pumps handles all our pump station maintenance across multiple sites. Always responsive, thorough reports, and they catch pump issues before they become emergencies. Great pump service.",
                      rating: 5
                    },
                    {
                      name: 'Emma Collins',
                      role: 'Business Owner',
                      company: 'Restaurant, Bray',
                      review: "Our pump failed on a busy weekend and Jack's Pumps saved us. Emergency pump response was fast, work was done professionally, and we were back operating quickly. Can't thank them enough.",
                      rating: 5
                    }
                  ].map((review, index) => (
                    <article key={index} className="bg-[#2A2A2A] p-6 rounded-xl border border-[#00D084]/20 hover:border-[#00D084] transition-all">
                      <div className="flex gap-1 mb-4">
                        {[1, 2, 3, 4, 5].map((star) => (
                          <i key={star} className="ri-star-fill text-xl text-[#00D084]" aria-hidden="true"></i>
                        ))}
                      </div>
                      <p className="text-gray-300 mb-6 leading-relaxed text-sm italic">"{review.review}"</p>
                      <div className="border-t border-gray-700 pt-4">
                        <p className="text-white font-semibold">{review.name}</p>
                        <p className="text-[#00D084] text-sm">{review.role}</p>
                        <p className="text-gray-400 text-xs">{review.company}</p>
                      </div>
                    </article>
                  ))}
                </div>
              </div>
            </div>
          </section>

          {/* Emergency CTA Banner */}
          <section className="bg-gradient-to-r from-[#00B870] to-[#00D084] py-16">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="flex flex-col lg:flex-row items-center justify-between gap-8">
                <div className="text-center lg:text-left">
                  <h2 className="text-3xl sm:text-4xl font-bold text-white mb-2">Need <strong>Emergency Pump Repairs</strong> in Ireland?</h2>
                  <p className="text-white/90 text-sm">Can't reach us? Submit details below - <strong>emergency pump repairs</strong> are prioritised</p>
                </div>
                <a href="tel:+35340454753" className="bg-white text-[#00D084] px-10 py-5 rounded-xl font-bold text-xl hover:shadow-2xl transition-all flex items-center gap-3 whitespace-nowrap">
                  <i className="ri-phone-fill text-2xl" aria-hidden="true"></i>
                  Call +353 404 54753
                </a>
              </div>
            </div>
          </section>

          {/* Contact Section */}
          <section id="contact" className="bg-white py-20" aria-labelledby="contact-heading">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="grid grid-cols-1 lg:grid-cols-5 gap-12">
                {/* Left Column - Contact Methods */}
                <div className="lg:col-span-2">
                  <h2 id="contact-heading" className="text-4xl font-bold text-[#1A1A1A] mb-8">Contact Us for <strong>Pump Repairs</strong></h2>
                  
                  <div className="space-y-4">
                    <a href="tel:+35340454753" className="w-full block bg-[#00D084] text-white p-6 rounded-xl hover:bg-[#00B870] transition-all" aria-label="Call Jack's Pumps for pump repairs Ireland">
                      <div className="flex items-center gap-4">
                        <i className="ri-phone-fill text-3xl" aria-hidden="true"></i>
                        <div className="text-left">
                          <p className="text-sm opacity-90">Tap to call for pump repairs</p>
                          <p className="text-2xl font-bold">+353 404 54753</p>
                        </div>
                      </div>
                    </a>

                    <a href="https://wa.me/35340454753" className="block bg-[#25D366] text-white p-6 rounded-xl hover:bg-[#20BA5A] transition-all" rel="noopener noreferrer" aria-label="WhatsApp Jack's Pumps for pump services">
                      <div className="flex items-center gap-4">
                        <i className="ri-whatsapp-fill text-3xl" aria-hidden="true"></i>
                        <div>
                          <p className="text-sm opacity-90">Message us about pump repairs</p>
                          <p className="text-xl font-bold">WhatsApp</p>
                        </div>
                      </div>
                    </a>

                    <a href="mailto:info@jackspumps.ie" className="block bg-[#1A1A1A] text-white p-6 rounded-xl hover:bg-[#2A2A2A] transition-all" aria-label="Email Jack's Pumps for pump services">
                      <div className="flex items-center gap-4">
                        <i className="ri-mail-fill text-3xl" aria-hidden="true"></i>
                        <div>
                          <p className="text-sm opacity-90">Email us about pump services</p>
                          <p className="text-lg font-bold">info@jackspumps.ie</p>
                        </div>
                      </div>
                    </a>
                  </div>

                  <div className="mt-6 p-4 bg-red-50 border-l-4 border-red-500 rounded">
                    <p className="text-sm text-red-800 font-medium">
                      <i className="ri-error-warning-fill mr-2" aria-hidden="true"></i>
                      <strong>Emergency pump repair?</strong> Call immediately or submit form - we prioritise urgent pump issues
                    </p>
                  </div>
                </div>

                {/* Right Column - Contact Form */}
                <div className="lg:col-span-3">
                  <ContactForm />
                </div>
              </div>
            </div>
          </section>

          {/* Footer */}
          <footer className="bg-[#1A1A1A] rounded-t-3xl">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-12 mb-12">
                {/* Left Column */}
                <div>
                  <img 
                    src="https://static.readdy.ai/image/dc999f92be6fe23ab0f2c081255e30e9/be41bd6c16e3f7ca48ba1bf859508677.jpeg" 
                    alt="Jack's Pumps Logo - Pump Station Repairs Ireland" 
                    title="Jack's Pumps - Ireland's #1 Pump Repair Specialists"
                    className="h-40 w-auto mb-4 bg-white rounded-lg p-2"
                    width="160"
                    height="160"
                    loading="lazy"
                  />
                  <p className="text-[#00D084] text-sm mb-3">#1 Pump Station Specialists • Nationwide Coverage</p>
                  <p className="text-gray-400 text-sm leading-relaxed mb-6">
                    Professional <strong>pump station repairs</strong>, <strong>pump servicing</strong>, and <strong>pump installations across Ireland</strong>. 24/7 emergency pump response and planned pump maintenance for domestic, commercial, and industrial sites.
                  </p>
                </div>

                {/* Middle Column */}
                <nav aria-label="Footer navigation">
                  <h3 className="text-white text-lg font-bold mb-4">Pump Services</h3>
                  <div className="space-y-3">
                    <Link to="/services" className="block text-gray-400 hover:text-[#00D084] transition-colors">All Pump Services</Link>
                    <Link to="/services" className="block text-gray-400 hover:text-[#00D084] transition-colors">Emergency Pump Repairs</Link>
                    <Link to="/services" className="block text-gray-400 hover:text-[#00D084] transition-colors">Pump Maintenance</Link>
                    <Link to="/contact" className="block text-gray-400 hover:text-[#00D084] transition-colors">Contact</Link>
                  </div>
                </nav>

                {/* Right Column */}
                <div>
                  <h3 className="text-white font-semibold mb-2">Contact for Pump Repairs</h3>
                  <a href="tel:+35340454753" className="block text-white text-xl font-semibold mb-2 hover:text-[#00D084] transition-colors" aria-label="Call Jack's Pumps for pump repairs">
                    <strong>+353 404 54753</strong>
                  </a>
                  <a href="mailto:info@jackspumps.ie" className="block text-gray-400 mb-6 hover:text-[#00D084] transition-colors" aria-label="Email Jack's Pumps">
                    info@jackspumps.ie
                  </a>
                  
                  <h4 className="text-white font-semibold mb-2">Pump Service Areas</h4>
                  <p className="text-gray-400 text-sm"><strong>Dublin, Wicklow, Wexford, Kildare, Meath, Louth, Carlow</strong> & Nationwide</p>
                </div>
              </div>

              <div className="border-t border-[#00D084] pt-8">
                <div className="flex flex-col md:flex-row justify-between items-center gap-4">
                  <div className="flex gap-4">
                    <a href="#" className="text-white hover:text-[#00D084] transition-colors" aria-label="Follow Jack's Pumps on LinkedIn" rel="nofollow">
                      <i className="ri-linkedin-fill text-2xl" aria-hidden="true"></i>
                    </a>
                    <a href="#" className="text-white hover:text-[#00D084] transition-colors" aria-label="Follow Jack's Pumps on Facebook" rel="nofollow">
                      <i className="ri-facebook-fill text-2xl" aria-hidden="true"></i>
                    </a>
                  </div>
                  
                  <p className="text-gray-400 text-sm">© 2025 Jack's Pumps. All rights reserved. | Pump Repairs Ireland</p>
                  
                  <div className="flex gap-4 text-sm">
                    <a href="https://readdy.ai/?ref=logo" target="_blank" rel="noopener noreferrer nofollow" className="text-gray-400 hover:text-[#00D084] transition-colors whitespace-nowrap">
                      Powered by Readdy
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </footer>

          {/* Sticky Mobile Contact Bar */}
          <div className="fixed bottom-0 left-0 right-0 bg-[#1A1A1A] border-t-2 border-[#00D084] shadow-2xl z-50 lg:hidden" role="navigation" aria-label="Mobile contact options for pump repairs">
            <div className="grid grid-cols-3 h-18">
              <a href="tel:+35340454753" className="flex flex-col items-center justify-center py-3 hover:bg-[#2A2A2A] transition-colors" aria-label="Call Jack's Pumps for pump repairs">
                <i className="ri-phone-fill text-2xl text-[#00D084] mb-1" aria-hidden="true"></i>
                <span className="text-white text-xs font-medium">Call</span>
              </a>
              <a href="https://wa.me/35340454753" className="flex flex-col items-center justify-center py-3 hover:bg-[#2A2A2A] transition-colors border-x border-gray-700" rel="noopener noreferrer" aria-label="WhatsApp Jack's Pumps for pump services">
                <i className="ri-whatsapp-fill text-2xl text-[#00D084] mb-1" aria-hidden="true"></i>
                <span className="text-white text-xs font-medium">Message</span>
              </a>
              <a href="#contact" className="flex flex-col items-center justify-center py-3 hover:bg-[#2A2A2A] transition-colors" aria-label="Request a pump repair quote">
                <i className="ri-file-list-3-line text-2xl text-[#00D084] mb-1" aria-hidden="true"></i>
                <span className="text-white text-xs font-medium">Quote</span>
              </a>
            </div>
          </div>
        </main>
      </div>
    </>
  );
}

function ContactForm() {
  const [formData, setFormData] = useState({
    name: '',
    mobile: '',
    location: '',
    issueType: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      formBody.append('access_key', 'd8a90d32-fad8-4c33-b2f6-b27dc380d5fc');
      formBody.append('name', formData.name);
      formBody.append('mobile', formData.mobile);
      formBody.append('location', formData.location);
      formBody.append('issueType', formData.issueType);
      formBody.append('message', formData.message);
      formBody.append('subject', 'Pump Repair Enquiry - Jack\'s Pumps Website');

      const response = await fetch('https://api.web3forms.com/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString()
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({ name: '', mobile: '', location: '', issueType: '', message: '' });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form id="pump-repair-enquiry-form" data-readdy-form onSubmit={handleSubmit} className="bg-[#F8F8F8] p-8 rounded-xl">
      <h3 className="text-2xl font-bold text-[#1A1A1A] mb-2">Request <strong>Pump Repair</strong> Quote</h3>
      <p className="text-gray-600 text-sm mb-6">Fill in your details and we'll get back to you about your pump issue</p>
      
      <div className="space-y-4">
        <div>
          <label htmlFor="contact-name" className="block text-sm font-medium text-gray-700 mb-2">Your Name *</label>
          <input
            type="text"
            id="contact-name"
            name="name"
            placeholder="Your Name"
            required
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            className="w-full px-4 py-4 bg-white border border-gray-300 rounded-lg focus:outline-none focus:border-[#00D084] text-sm"
            aria-required="true"
          />
        </div>

        <div>
          <label htmlFor="contact-mobile" className="block text-sm font-medium text-gray-700 mb-2">Mobile Number *</label>
          <input
            type="tel"
            id="contact-mobile"
            name="mobile"
            placeholder="Mobile Number"
            required
            value={formData.mobile}
            onChange={(e) => setFormData({ ...formData, mobile: e.target.value })}
            className="w-full px-4 py-4 bg-white border border-gray-300 rounded-lg focus:outline-none focus:border-[#00D084] text-sm"
            aria-required="true"
          />
        </div>

        <div>
          <label htmlFor="contact-location" className="block text-sm font-medium text-gray-700 mb-2">Location *</label>
          <input
            type="text"
            id="contact-location"
            name="location"
            placeholder="Dublin, Wicklow, Wexford, etc."
            required
            value={formData.location}
            onChange={(e) => setFormData({ ...formData, location: e.target.value })}
            className="w-full px-4 py-4 bg-white border border-gray-300 rounded-lg focus:outline-none focus:border-[#00D084] text-sm"
            aria-required="true"
          />
        </div>

        <div>
          <label htmlFor="contact-issue" className="block text-sm font-medium text-gray-700 mb-2">Pump Issue Type *</label>
          <select
            id="contact-issue"
            name="issueType"
            required
            value={formData.issueType}
            onChange={(e) => setFormData({ ...formData, issueType: e.target.value })}
            className="w-full px-4 py-4 bg-white border border-gray-300 rounded-lg focus:outline-none focus:border-[#00D084] text-sm cursor-pointer"
            aria-required="true"
          >
            <option value="">Select Pump Issue Type</option>
            <option value="Emergency Pump Repair" className="text-red-600 font-semibold">🚨 Emergency Pump Repair</option>
            <option value="Blocked Pump">Blocked Pump / De-ragging</option>
            <option value="Pump Servicing">Pump Servicing / Maintenance</option>
            <option value="Pump Installation">Pump Installation</option>
            <option value="Pump Quote">Quote Request</option>
          </select>
        </div>

        <div>
          <label htmlFor="contact-message" className="block text-sm font-medium text-gray-700 mb-2">Describe Your Pump Issue *</label>
          <textarea
            id="contact-message"
            name="message"
            placeholder="Brief description of your pump issue..."
            rows={4}
            maxLength={500}
            required
            value={formData.message}
            onChange={(e) => setFormData({ ...formData, message: e.target.value })}
            className="w-full px-4 py-4 bg-white border border-gray-300 rounded-lg focus:outline-none focus:border-[#00D084] resize-none text-sm"
            aria-required="true"
          ></textarea>
          <p className="text-xs text-gray-500 mt-1">{formData.message.length}/500 characters</p>
        </div>

        <button
          type="submit"
          disabled={isSubmitting}
          className="w-full bg-[#00D084] text-white py-4 rounded-lg font-bold text-lg hover:bg-[#00B870] transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
          aria-label="Send pump repair enquiry"
        >
          {isSubmitting ? 'Sending...' : 'Send Pump Repair Enquiry'}
        </button>

        {submitStatus === 'success' && (
          <div className="bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded-lg text-sm" role="alert">
            <i className="ri-checkbox-circle-fill mr-2" aria-hidden="true"></i>
            Thank you! We'll be in touch about your pump repair shortly.
          </div>
        )}

        {submitStatus === 'error' && (
          <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg text-sm" role="alert">
            <i className="ri-error-warning-fill mr-2" aria-hidden="true"></i>
            Something went wrong. Please try calling us directly for pump repairs.
          </div>
        )}

        <p className="text-xs text-gray-500 text-center">All pump repair enquiries captured 24/7</p>
      </div>
    </form>
  );
}
